// Step 18 — AI shell context.
//
// Single-source workspace/deal context for every route rendered inside
// AIShell. Wraps `/ai/context/` (the same endpoint the existing
// AIWorkspace already uses) so we don't build a parallel context
// system.
//
// What it exposes:
//   context             — raw /ai/context/ payload
//   loading             — first-load flag
//   error               — string or null
//   refresh()           — re-fetch /ai/context/
//   workspaces          — context.workspaces || []
//   companies           — context.companies   || []
//   user                — context.user        || null
//   companyIntel        — context.company_intelligence
//   dealIntel           — context.deal_intelligence
//   activeWorkspace     — resolved from URL param :workspaceId when
//                         present, else context.active_workspace
//   activeWorkspaceId   — number or null
//
// Deliberately does NOT own the workspace-list re-fetch cadence for
// sub-endpoints (contracts, actions, notifications) — those still fetch
// per-page as they always have. This provider is a thin identity/
// context layer, not a data cache.

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { fetchAIContext } from "../api";

const AIWorkspaceContextObject = createContext(null);

export function AIWorkspaceProvider({ children }) {
  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async ({ signal } = {}) => {
    setLoading(true);
    setError(null);
    try {
      const ctx = await fetchAIContext({ signal });
      setContext(ctx);
    } catch (e) {
      if (e?.name === "AbortError") return;
      setError(e?.body?.detail || e.message || "Kunde inte ladda AI-kontext");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    load({ signal: controller.signal });
    return () => controller.abort();
  }, [load]);

  const value = useMemo(
    () => ({
      context,
      loading,
      error,
      refresh: load,
      workspaces: context?.workspaces || [],
      companies: context?.companies || [],
      user: context?.user || null,
      companyIntel: context?.company_intelligence || null,
      dealIntel: context?.deal_intelligence || null,
      // activeWorkspace resolution happens inside the hook — it needs
      // access to URL params which aren't reliable at provider level.
    }),
    [context, loading, error, load],
  );

  return (
    <AIWorkspaceContextObject.Provider value={value}>
      {children}
    </AIWorkspaceContextObject.Provider>
  );
}

// Hook consumed by every AI page. Resolves activeWorkspace from URL
// (:workspaceId) when the route is deal-scoped, else falls back to the
// server-selected active_workspace.
export function useAIWorkspace() {
  const ctx = useContext(AIWorkspaceContextObject);
  const params = useParams();
  if (!ctx) {
    throw new Error("useAIWorkspace must be used inside <AIWorkspaceProvider>");
  }

  const routeWsId = params?.workspaceId ? Number(params.workspaceId) : null;
  const workspaces = ctx.workspaces;

  const activeWorkspace =
    (routeWsId != null &&
      workspaces.find((w) => Number(w.id) === routeWsId)) ||
    ctx.context?.active_workspace ||
    null;

  const activeWorkspaceId = activeWorkspace?.id ?? null;

  return {
    ...ctx,
    activeWorkspace,
    activeWorkspaceId,
  };
}
